import {
  Box,
  Button,
  Flex,
  Icon,
  Option,
  Select,
  Tag,
  Text,
} from '@tonic-one/react';
import { DeleteIcon } from '@tonic-one/react-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import { ApprovedHeaderFieldsItem } from '../../types/globalSetting';
import { FormInput } from '../Common/FormComponent';
import { FlexOption } from '../EditableList';
import useHeaderFieldListStyle from './style';
import SmartTooltip from '@Report/SmartTooltip';
import { isRegExp, validateRegExp } from '@Utils/validate';
import { safeDecodeURIComponent } from '@Utils/common';

export const FlexList = ({ style, ...props }: { [key: string]: any }) => {
  useWhyDidYouUpdate('HeaderFieldList-FlexList', { style, ...props });
  const styles = useHeaderFieldListStyle();
  const { flexListStyle } = styles;
  const newStyle = { ...flexListStyle, ...style };
  return <Flex style={newStyle} {...props} />;
};

function HeaderFieldItem(props: {
  field: string;
  setKey: (key: string) => void;
  value: string;
  setValue: (value: string) => void;
  operators: {
    value: string;
    name: string;
  }[];
  operator: string;
  setOperator: (operator: string) => void;
  disabled: boolean;
  onDelete?: (item: { key: string; operator: string; value: string }) => void;
  [key: string]: any;
}) {
  const {
    maxLength,
    field,
    value,
    operator,
    onDelete,
    setKey,
    setValue,
    setOperator,
    operators,
    disabled,
    ...restProps
  } = props;
  const item = { key: field, value, operator };
  const { t } = useTranslation();
  return (
    <Flex alignItems="center" mb="3x" {...restProps}>
      <FormInput
        ml="3x"
        value={field}
        width="25%"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setKey(e.target.value);
        }}
        maxLength={maxLength}
        placeholder={t('common.label.enterName')}
        disabled={disabled}
      ></FormInput>
      <Select
        value={operator}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          setOperator(e.target.value);
        }}
        width="25%"
        ml="3x"
        disabled={disabled}
      >
        {operators.map((item) => (
          <Option value={item.value} key={item.value}>
            {item.name}
          </Option>
        ))}
      </Select>
      <FormInput
        ml="6x"
        width="35%"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue(e.target.value);
        }}
        placeholder={t('common.label.enterValue')}
        disabled={disabled}
        maxLength={maxLength}
      ></FormInput>
      {onDelete && (
        <Button
          ml="3x"
          onClick={() => {
            onDelete(item);
          }}
          variant="ghost"
        >
          <Icon as={DeleteIcon} />
        </Button>
      )}
    </Flex>
  );
}
function HeaderFieldList(props: {
  onDelete: (item: ApprovedHeaderFieldsItem) => void;
  onAdd: (item: ApprovedHeaderFieldsItem) => void;
  list: any[];
  disabled: boolean;
  limit: number;
  description: string;
  width: string;
  size: number;
  headerMaxLength: number;
  operators: {
    value: string;
    name: string;
  }[];
}) {
  const { descriptionStyle } = useHeaderFieldListStyle();
  const {
    onDelete,
    onAdd,
    list,
    disabled,
    limit,
    description,
    size,
    width,
    operators,
    headerMaxLength,
  } = props;
  const [key, setKey] = React.useState<string>('');
  const [value, setValue] = React.useState<string>('');
  const [operator, setOperator] = React.useState(operators[0].value);
  const { t } = useTranslation();
  const height = React.useMemo(() => {
    return `${size * 32 + 32 + 12 + 17}px`;
  }, [size]);
  const action = React.useMemo(() => {
    return {
      delete: {
        disabled,
        handler: onDelete,
      },
    };
  }, [onDelete, disabled]);
  const prefixRef = React.useRef(Math.random().toString(36).slice(4));
  const validateInput = ({
    key,
    operator,
    value,
  }: ApprovedHeaderFieldsItem) => {
    if (
      !key ||
      !operator ||
      !value ||
      key.length > headerMaxLength ||
      value.length > headerMaxLength
    ) {
      window.showNotification({
        titleMsg: t(
          'globalSetting.approvedHeaderFields.addError.invalidInput.title'
        ),
        bodyMsg: t(
          'globalSetting.approvedHeaderFields.addError.invalidInput.content',
          { count: headerMaxLength }
        ),
        severity: 'error',
      });
      return false;
    } else if (
      validateRegExp(key, /[*]/) ||
      validateRegExp(value, /[*]/) ||
      isRegExp(key) ||
      isRegExp(value)
    ) {
      window.showNotification({
        titleMsg: t(
          'globalSetting.approvedHeaderFields.addError.invalidInput.title'
        ),
        bodyMsg: t(
          'globalSetting.approvedHeaderFields.addError.notSupport.content'
        ),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };
  const checkDuplicateHeaderField = ({
    key,
    operator,
    value,
  }: ApprovedHeaderFieldsItem) => {
    const tmp = JSON.stringify(list);
    if (tmp.includes(JSON.stringify({ key, operator, value }))) {
      window.showNotification({
        titleMsg: t('globalSetting.HeaderFields.duplicate.header'),
        bodyMsg: t('globalSetting.HeaderFields.duplicate.content'),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };
  const reachLimit = () => {
    if (list.length >= limit) {
      window.showNotification({
        titleMsg: t(
          'globalSetting.approvedHeaderFields.addError.reachLimit.title'
        ),
        bodyMsg: t(
          'globalSetting.approvedHeaderFields.addError.reachLimit.content',
          { count: limit }
        ),
        severity: 'error',
      });
      return false;
    } else {
      return true;
    }
  };
  function removeLastColon(str: string) {
    return str.replace(/[:\s]+$/, '');
  }
  useWhyDidYouUpdate('HeaderFieldList', props);
  return (
    <Box>
      <FlexList width={width} direction="column" height={height}>
        <Flex alignItems="flex-start">
          <HeaderFieldItem
            {...{
              field: key,
              value,
              operator,
              setKey,
              setValue,
              setOperator,
              operators,
              maxLength: headerMaxLength,
            }}
            disabled={disabled || false}
            width="95%"
          />
          <Button
            onClick={() => {
              /**
               * CAS Backend not allow the name have colon on the end
               */
              const tempKey = removeLastColon(key);
              if (
                !validateInput({ key: tempKey, operator, value }) ||
                !checkDuplicateHeaderField({ key: tempKey, operator, value }) ||
                !reachLimit()
              ) {
                return;
              }
              onAdd({
                key: tempKey.trim(),
                operator,
                value: value.trim(),
              });
              setKey('');
              setValue('');
              setOperator(operators[0].value);
            }}
            disabled={disabled || false}
          >
            {t('common.action.label.add')}
          </Button>
        </Flex>

        {description && (
          <Flex>
            <Text style={descriptionStyle}>{description}</Text>
          </Flex>
        )}
        {/* {list.map((item, index) => (
          <HeaderFieldItem
            key={`exchange-online-headerfield-${index}`}
            field={item.key}
            value={item.value}
            operator={item.operator}
            onDelete={onDelete}
            disabled={disabled || false}
            {...{ setKey, setOperator, setValue, operators }}
          />
        ))} */}
        {list.map((item, index) => (
          <FlexOption
            action={action}
            value={item}
            key={`${prefixRef.current}-${index}-${item.value}`}
          >
            {index !== 0 ? (
              <Tag mr={8}>{t('common.operator.or')}</Tag>
            ) : undefined}
            <Text maxWidth={600}>
              <SmartTooltip>{`${safeDecodeURIComponent(item.key)} ${
                item.operator
              } ${safeDecodeURIComponent(item.value)}`}</SmartTooltip>
            </Text>
          </FlexOption>
        ))}
      </FlexList>
    </Box>
  );
}

export default HeaderFieldList;
